import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getMonitorEntryAssessment, MonitorEntryAssessment } from '../../services/api';

const share = (done: number, total: number) => (total ? Math.round((done * 100) / total) : 0);

// "0%" next to a non-zero count reads as nobody, so show a sliver as "<1%".
const shareLabel = (done: number, total: number) =>
  done > 0 && share(done, total) === 0 ? '<1%' : `${share(done, total)}%`;

// One hue, light -> dark: the more of a class has taken it, the darker the cell.
// Violet to match the rest of the monitor. Text flips to white on the two dark
// steps so every label keeps contrast.
const BINS = [
  { label: 'Not started', test: (done: number) => done === 0, bg: '#F4F2FB', ink: 'text-slate-400' },
  { label: 'Under 25%', test: (_d: number, p: number) => p < 25, bg: '#E4DCFD', ink: 'text-slate-800' },
  { label: '25–49%', test: (_d: number, p: number) => p < 50, bg: '#C4B5FD', ink: 'text-slate-900' },
  { label: '50–74%', test: (_d: number, p: number) => p < 75, bg: '#7C3AED', ink: 'text-white' },
  { label: '75% and up', test: () => true, bg: '#5B21B6', ink: 'text-white' },
];
const binFor = (done: number, total: number) => {
  const p = share(done, total);
  return BINS.find((b) => b.test(done, p))!;
};

// Curiosity levels, lowest first: the same violet ramp as the teacher card.
const LEVEL_COLORS: Record<string, string> = {
  emerging: '#DDD6FE',
  developing: '#A78BFA',
  curious: '#7C3AED',
  highly_curious: '#4C1D95',
};

interface Cell {
  total: number;
  done: number;
  scoreSum: number; // sum of curiosity scores, so merged cells average correctly
}
const emptyCell = (): Cell => ({ total: 0, done: 0, scoreSum: 0 });
const add = (a: Cell, b: Cell): Cell => ({
  total: a.total + b.total,
  done: a.done + b.done,
  scoreSum: a.scoreSum + b.scoreSum,
});
const avg = (c: Cell) => (c.done ? Math.round(c.scoreSum / c.done) : null);

interface Tip {
  x: number;
  y: number;
  title: string;
  cell: Cell;
}

const CompletionRing: React.FC<{ pct: number; label: string }> = ({ pct, label }) => {
  const R = 40;
  const C = 2 * Math.PI * R;
  const p = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative h-[120px] w-[120px] shrink-0">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden>
        <circle cx="50" cy="50" r={R} fill="none" stroke="#EDEBFA" strokeWidth="11" />
        {p > 0 && (
          <circle
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke="#7C3AED"
            strokeWidth="11"
            strokeLinecap="round"
            strokeDasharray={`${(p / 100) * C} ${C}`}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold text-slate-900">{label}</span>
        <span className="text-[11px] text-slate-400">have taken it</span>
      </div>
    </div>
  );
};

/** Programme-wide entry assessment progress as a school x grade grid: each
 *  cell is one class-year in one school, shaded by how much of it has taken
 *  the assessment. All-time, not tied to the period picker: each student takes
 *  it once, so a window would only hide who took it earlier. */
const EntryAssessmentPanel: React.FC = () => {
  const [data, setData] = useState<MonitorEntryAssessment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<'most' | 'az'>('most');
  const [tip, setTip] = useState<Tip | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('monitorToken');
    if (!token) return;
    getMonitorEntryAssessment(token)
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  const grid = useMemo(() => {
    if (!data) return null;
    const grades = data.grades.length ? data.grades : Array.from(new Set(data.by_school_grade.map((c) => c.grade))).sort();
    const bySchool = new Map<string, Map<number, Cell>>();
    for (const c of data.by_school_grade) {
      const row = bySchool.get(c.school) ?? new Map<number, Cell>();
      const cell: Cell = {
        total: c.total_students,
        done: c.completed,
        scoreSum: (c.average_score ?? 0) * c.completed,
      };
      row.set(c.grade, add(row.get(c.grade) ?? emptyCell(), cell));
      bySchool.set(c.school, row);
    }
    const rows = Array.from(bySchool.entries()).map(([school, cells]) => ({
      school,
      cells,
      all: Array.from(cells.values()).reduce(add, emptyCell()),
    }));
    rows.sort((a, b) =>
      sort === 'az'
        ? a.school.localeCompare(b.school)
        : share(b.all.done, b.all.total) - share(a.all.done, a.all.total) ||
          b.all.done - a.all.done ||
          a.school.localeCompare(b.school),
    );
    const gradeTotals = new Map<number, Cell>(
      grades.map((g) => [g, rows.reduce((acc, r) => add(acc, r.cells.get(g) ?? emptyCell()), emptyCell())]),
    );
    const everything = rows.reduce((acc, r) => add(acc, r.all), emptyCell());
    return { grades, rows, gradeTotals, everything };
  }, [data, sort]);

  const showTip = (e: React.MouseEvent | React.FocusEvent, title: string, cell: Cell) => {
    const wrap = wrapRef.current?.getBoundingClientRect();
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (!wrap) return;
    setTip({ x: box.left - wrap.left + box.width / 2, y: box.top - wrap.top, title, cell });
  };

  if (!data && !error) return null;

  const cols = grid ? `minmax(128px, 1.7fr) repeat(${grid.grades.length}, minmax(58px, 1fr)) minmax(92px, 1.2fr)` : '';

  const heatCell = (key: React.Key, title: string, cell: Cell | undefined, strong?: boolean) => {
    if (!cell || cell.total === 0) {
      return (
        <div key={key} className="flex h-11 items-center justify-center rounded-lg border border-dashed border-slate-200 text-xs text-slate-300">
          —
        </div>
      );
    }
    const bin = binFor(cell.done, cell.total);
    return (
      <div
        key={key}
        tabIndex={0}
        onMouseEnter={(e) => showTip(e, title, cell)}
        onFocus={(e) => showTip(e, title, cell)}
        onMouseLeave={() => setTip(null)}
        onBlur={() => setTip(null)}
        aria-label={`${title}: ${cell.done} of ${cell.total} have taken it`}
        className={`flex h-11 cursor-default flex-col items-center justify-center rounded-lg outline-none ring-violet-400 transition-transform hover:scale-[1.04] focus-visible:ring-2 ${bin.ink} ${
          strong ? 'font-bold' : ''
        }`}
        style={{ background: bin.bg }}
      >
        <span className="text-sm font-bold leading-none">{shareLabel(cell.done, cell.total)}</span>
        <span className="mt-0.5 text-[10px] leading-none opacity-80">
          {cell.done}/{cell.total}
        </span>
      </div>
    );
  };

  const totalBar = (title: string, cell: Cell, strong?: boolean) => {
    const pct = share(cell.done, cell.total);
    return (
      <div
        tabIndex={0}
        onMouseEnter={(e) => showTip(e, title, cell)}
        onFocus={(e) => showTip(e, title, cell)}
        onMouseLeave={() => setTip(null)}
        onBlur={() => setTip(null)}
        className="flex h-11 flex-col justify-center gap-1 rounded-lg px-1 outline-none ring-violet-400 focus-visible:ring-2"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-sm text-slate-900 ${strong ? 'font-extrabold' : 'font-bold'}`}>
            {shareLabel(cell.done, cell.total)}
          </span>
          <span className="text-[10px] tabular-nums text-slate-400">
            {cell.done.toLocaleString()}/{cell.total.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-[#EDEBFA]">
          <div
            className="h-full rounded-full bg-violet-600"
            style={{ width: cell.done > 0 ? `max(${pct}%, 4px)` : 0 }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="mt-5 rounded-2xl border border-[#E9E6F4] bg-white p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-slate-900">Curiosity assessment</h2>
          <p className="text-[11px] text-slate-400">
            Taken once by each student before their first chat
            {data && data.grades.length > 0 && ` · grades ${data.grades.join(', ')}`} · all time
          </p>
        </div>
        {grid && grid.rows.length > 1 && (
          <div className="flex rounded-lg border border-slate-200 p-0.5 text-xs" role="group" aria-label="Sort schools">
            {(
              [
                ['most', 'Most taken'],
                ['az', 'A–Z'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setSort(k)}
                aria-pressed={sort === k}
                className={`rounded-md px-2.5 py-1 font-medium ${
                  sort === k ? 'bg-violet-600 text-white' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : data && grid ? (
        <>
          {data.sample && (
            <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The papers are still sample questions, so these scores are for testing the flow only.
            </p>
          )}

          {/* Headline */}
          <div className="mb-5 flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-5">
              <CompletionRing
                pct={share(data.completed, data.total_students)}
                label={shareLabel(data.completed, data.total_students)}
              />
              <div>
                <p className="text-3xl font-extrabold text-slate-900">
                  {data.completed.toLocaleString()}
                  <span className="text-lg font-medium text-slate-400"> / {data.total_students.toLocaleString()}</span>
                </p>
                <p className="text-xs text-slate-500">students have taken it</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="text-2xl font-extrabold text-slate-900">
                  {data.average_score == null ? '—' : data.average_score}
                  <span className="text-base font-medium text-slate-400">/100</span>
                </p>
                <p className="text-xs text-slate-500">average curiosity score</p>
              </div>
              <div>
                <p className="text-2xl font-extrabold text-slate-900">
                  {grid.rows.filter((r) => r.all.done > 0).length}
                  <span className="text-base font-medium text-slate-400"> / {grid.rows.length}</span>
                </p>
                <p className="text-xs text-slate-500">schools started</p>
              </div>
            </div>
          </div>

          {/* Curiosity level mix of everyone who has taken it */}
          {data.completed > 0 && (
            <div className="mb-5">
              <p className="mb-1.5 text-[11px] font-semibold text-slate-600">Curiosity levels of students who have taken it</p>
              <div className="mb-1.5 flex h-3 overflow-hidden rounded-full bg-slate-100">
                {data.levels
                  .filter((l) => l.count > 0)
                  .map((l) => (
                    <div
                      key={l.key}
                      title={`${l.label}: ${l.count.toLocaleString()}`}
                      className="border-r-2 border-white last:border-r-0"
                      style={{ width: `${(l.count * 100) / data.completed}%`, background: LEVEL_COLORS[l.key] }}
                    />
                  ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                {data.levels.map((l) => (
                  <span key={l.key} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: LEVEL_COLORS[l.key] }} />
                    {l.label}
                    <span className="font-semibold text-slate-800">
                      {l.count.toLocaleString()} · {shareLabel(l.count, data.completed)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
            <span className="font-semibold text-slate-600">Share of students who have taken it:</span>
            {BINS.map((b) => (
              <span key={b.label} className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded" style={{ background: b.bg }} />
                {b.label}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded border border-dashed border-slate-300" />
              No students
            </span>
          </div>

          {/* School x grade grid */}
          <div ref={wrapRef} className="relative">
            <div className="overflow-x-auto pb-1">
              <div className="min-w-[520px]">
                <div className="grid items-end gap-1.5 pb-1.5" style={{ gridTemplateColumns: cols }}>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">School</span>
                  {grid.grades.map((g) => (
                    <span key={g} className="text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Grade {g}
                    </span>
                  ))}
                  <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">All grades</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  {grid.rows.map((r) => (
                    <div key={r.school} className="grid items-center gap-1.5" style={{ gridTemplateColumns: cols }}>
                      <span className="truncate pr-2 text-sm text-slate-800" title={r.school}>
                        {r.school}
                      </span>
                      {grid.grades.map((g) => heatCell(g, `${r.school} · Grade ${g}`, r.cells.get(g)))}
                      {totalBar(`${r.school} · all grades`, r.all)}
                    </div>
                  ))}

                  <div
                    className="mt-1 grid items-center gap-1.5 border-t border-slate-100 pt-2.5"
                    style={{ gridTemplateColumns: cols }}
                  >
                    <span className="text-sm font-bold text-slate-900">All schools</span>
                    {grid.grades.map((g) => heatCell(g, `All schools · Grade ${g}`, grid.gradeTotals.get(g), true))}
                    {totalBar('All schools · all grades', grid.everything, true)}
                  </div>
                </div>
              </div>
            </div>

            {tip && (
              <div
                className="pointer-events-none absolute z-20 w-max max-w-[240px] -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg"
                style={{ left: tip.x, top: tip.y - 6 }}
                role="tooltip"
              >
                <p className="font-semibold">{tip.title}</p>
                <p className="mt-0.5 text-slate-300">
                  {tip.cell.done.toLocaleString()} of {tip.cell.total.toLocaleString()} students have taken it (
                  {shareLabel(tip.cell.done, tip.cell.total)})
                </p>
                <p className="text-slate-300">
                  Average curiosity score: {avg(tip.cell) == null ? '—' : `${avg(tip.cell)}/100`}
                </p>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default EntryAssessmentPanel;
